// src/components/Swimming/SwimmingActivityModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import SwimmingDetailsForm from './SwimmingDetailsForm';
import FileUploadComponent from '../FileUploadComponent';
import WellnessForm from '../ActivityDetails/WellnessForm';
import PlannedVsCompletedSwimming from './PlannedVsCompletedSwimming';
import LapsTableSwimming from './LapsTableSwimming';
import SwimmingHeartRateHistogram from './SwimmingHeartRateHistogram';
import StrengthHeartRateHistogram from '../Strength/StrengthHeartRateHistogram';
import SwimmingPhysiologicalAnalysis from './SwimmingPhysiologicalAnalysis';
import SwimmingMetricsProcessor from './SwimmingMetricsProcessor';
import SwimmingSummary from '../Swimming/SwimmingSummary';
import SwimmingActions from './SwimmingActions';
import '../styles/SwimmingActivityModal.css';
import SwimmingDashboard from '../Swimming/SwimmingDashboard';
import ZoneDistributionChartSwimming from './ZoneDistributionChartSwimming';
import { useTranslation } from '../../i18n';
import moment from 'moment';
import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import '../styles/ActivityDetailsModal.css';
import SessionAiSummaryPanel from '../Insights/SessionAiSummaryPanel';

/* ---------------- Helpers ---------------- */
const pad = (n) => String(n).padStart(2, '0');
const normalizeType = (raw = '') => {
  const t = String(raw).toLowerCase();
  if (t.includes('nad')) return 'nadar';
  if (t.includes('cicl') || t.includes('bike')) return 'ciclismo';
  if (t.includes('fuer') || t.includes('strength') || t.includes('gym')) return 'fuerza';
  if (t.includes('cam') || t.includes('walk')) return 'caminar';
  if (t.includes('desc') || t.includes('rest')) return 'descanso';
  if (t.includes('cor') || t.includes('run')) return 'correr';
  return 'otro';
};
const toHHMMSS = (input) => {
  if (!input && input !== 0) return '';
  const s = String(input).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const [h, m, sec = '00'] = s.split(':');
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  }
  if (/^\d+$/.test(s)) {
    const secs = parseInt(s, 10);
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), sec = secs % 60;
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  }
  let total = 0, m;
  const re = /(\d+(?:\.\d+)?)\s*(h|hr|hora|horas|m|min|minutos|s|seg|segundos)/gi;
  while ((m = re.exec(s))) {
    const val = parseFloat(m[1]); const u = m[2].toLowerCase();
    if (['h','hr','hora','horas'].includes(u)) total += val * 3600;
    else if (['m','min','minutos'].includes(u)) total += val * 60;
    else total += val;
  }
  return total ? toHHMMSS(String(Math.round(total))) : '';
};
const toKmString = (input) => {
  if (input == null) return '';
  let s = String(input).trim().toLowerCase().replace(',', '.');
  if (/^\d+(\.(\d+))?\s*m$/.test(s)) return String(parseFloat(s) / 1000);
  if (/^\d+(\.(\d+))?\s*k$/.test(s)) return String(parseFloat(s));
  const num = parseFloat(s);
  return Number.isFinite(num) ? String(num) : '';
};
const durationToSeconds = (hhmmss = '') => {
  const [h='0', m='0', s='0'] = String(hhmmss).split(':');
  return (+h)*3600 + (+m)*60 + (+s);
};
const computeEnd = (startIso, hhmmss) => {
  if (!startIso || !hhmmss) return startIso;
  const secs = durationToSeconds(hhmmss);
  return new Date(new Date(startIso).getTime() + secs*1000).toISOString();
};
const resolveAssignedTo = (explicit, fallbackId) =>
  explicit || fallbackId || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');

const initialActivityState = {
  title: '', description: '', preComments: '', postComments: '',
  planned: {}, completed: {}, metrics: {}
};

const SwimmingActivityModal = ({
  show, onHide, activityType, onSave, onDelete,
  fetchEvents, profilePicture, selectedDate, athleteId
}) => {
  const { t } = useTranslation();
  const [activity, setActivity] = useState(initialActivityState);
  const [summary, setSummary]   = useState(null);
  const userId = athleteId || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');

  useEffect(() => {
    if (activityType) {
      const planned = {
        ...(activityType.planned || {}),
        duracion:  toHHMMSS(activityType?.planned?.duracion || activityType?.planned?.duration || activityType?.duration || ''),
        distancia: toKmString(activityType?.planned?.distancia ?? activityType?.distance ?? ''),
      };
      setActivity({ ...initialActivityState, ...activityType, planned, completed: activityType.completed ? { ...activityType.completed } : {}, metrics: activityType.metrics || {} });
    } else {
      setActivity(initialActivityState);
    }
    return () => { setActivity(initialActivityState); setSummary(null); };
  }, [activityType]);

  useEffect(() => {
    if (!summary || !summary.completed || !summary.completed.fileId || summary.completed.fileId === activity.completed?.fileId) return;
    setActivity((prev) => ({
      ...prev,
      completed: { ...prev.completed, ...summary.completed, fileId: summary.completed.fileId || prev.completed?.fileId },
      metrics: summary.metrics || prev.metrics,
    }));
  }, [summary, activity.completed?.fileId]);

  const handleSave = () => {
    const isPlannedFilled = Object.values(activity.planned || {}).some((val) => val && val.toString().trim() !== '');
    const hasDescription  = activity.description?.trim() !== '';
    if (!isPlannedFilled && !hasDescription) {
      alert(t('swimming.validationPlanned'));
      return;
    }
    const assignedTo = resolveAssignedTo(activity.assignedTo, userId);
    if (!assignedTo) {
      alert(t('activityDetails.validationAthleteNotFound'));
      return;
    }
    const startIso = new Date(selectedDate || new Date()).toISOString();
    const dur      = toHHMMSS(activity.planned?.duracion || activity.duration || '');
    const dist     = toKmString(activity.planned?.distancia || activity.distance || '');
    const endIso   = dur ? computeEnd(startIso, dur) : startIso;
    const payload  = {
      ...activity, assignedTo,
      type: normalizeType(activity.type || activityType?.type || 'nadar'),
      date: startIso, start: startIso, end: endIso,
      duration: dur || '', distance: dist || '',
      planned: { ...(activity.planned || {}), duracion: dur || '', distancia: dist || '' },
      bgColor: activity.bgColor || '#37474F',
      external: !!activity.external
    };
    if (typeof onSave === 'function') onSave(payload);
    onHide();
  };

  const handleDelete = () => {
    const id = activityType?.id || activity?._id;
    if (typeof onDelete === 'function' && id) { onDelete(id); onHide(); }
  };

  const handleFileUpload = async (data) => {
    if (data.fileId) {
      setActivity((prev) => ({ ...prev, completed: { ...prev.completed, fileId: data.fileId } }));
      if (typeof fetchEvents === 'function') fetchEvents();
    }
  };

  const handleSummaryData = useCallback(({ session, completed, laps, records, metrics }) => {
    const fileId = completed?.fileId;
    setActivity((prev) => ({
      ...prev,
      completed: { ...prev.completed, ...completed, fileId: fileId || prev.completed?.fileId },
      metrics: metrics || {}
    }));
    const validLaps = laps?.filter(l => l?.total_distance > 0 || l?.distance > 0);
    setSummary({ session, completed: { ...completed, fileId }, laps: validLaps, records, metrics });
  }, []);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton className="tz20-activity-header">
        <div className="tz20-activity-title-wrap">
          <span className="tz20-activity-icon" aria-hidden="true"><img src={swimIcon} alt="" /></span>
          <div>
            <Modal.Title className="tz20-activity-title">
              {t('activityDetails.activityModalTitle', { title: activity.title || t('activityLabels.swimming') })}
            </Modal.Title>
            <div className="tz20-activity-meta">
              {activity?.date ? moment(activity.date).format('ddd D MMM YYYY') : moment().format('ddd D MMM YYYY')}
              {' · '}
              {activity?.completed?.fileId ? 'Archivo .fit cargado' : 'Sin archivo .fit'}
            </div>
          </div>
        </div>
        <div className="tz20-sport-tabs" aria-label="Deportes">
          <button type="button" aria-label="Correr"><img src={runningIcon} alt="" /></button>
          <button type="button" aria-label="Ciclismo"><img src={cyclingIcon} alt="" /></button>
          <button type="button" className="active" aria-label="Nadar"><img src={swimIcon} alt="" /></button>
          <button type="button" aria-label="Fuerza"><img src={strengthIcon} alt="" /></button>
        </div>
      </Modal.Header>

        <Modal.Body className="tz20-activity-body">
          <Row className="align-items-start mt-4">
            <Col md={6}>
              <SwimmingDetailsForm
                title={activity.title}
                setTitle={(title) => setActivity((prev) => ({ ...prev, title }))}
                description={activity.description}
                setDescription={(description) => setActivity((prev) => ({ ...prev, description }))}
                preComments={activity.preComments}
                setPreComments={(preComments) => setActivity((prev) => ({ ...prev, preComments }))}
                postComments={activity.postComments}
                setPostComments={(postComments) => setActivity((prev) => ({ ...prev, postComments }))}
              />
            </Col>
            <Col md={6}>
              <PlannedVsCompletedSwimming
                key={`planned-completed-${activity.completed?.fileId || 'none'}`}
                planned={activity.planned}
                completed={activity.completed}
                setPlanned={(newPlanned) => setActivity((prev) => ({ ...prev, planned: newPlanned }))}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col>
              
          {!activity.completed?.fileId && (
            <WellnessForm
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              date={activity?.date || activityType?.date}
            />
          )}
          <FileUploadComponent activityId={activityType?.id || 'undefined'} activityType="nadar" onFileUpload={handleFileUpload} />
            </Col>
          </Row>

          {activity.completed?.fileId && (
            <SwimmingMetricsProcessor key={activity.completed.fileId} fileId={activity.completed.fileId} onSummaryData={handleSummaryData} />
          )}

          {activity.completed?.fileId && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('swimming.activitySummary')}</h2>
              <SwimmingSummary key={`summary-${activity.completed.fileId}`} fileId={activity.completed.fileId} onSummaryData={(summaryData) => setSummary(summaryData)} />
            </div>
          )}

          {summary?.completed && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('swimming.physiologicalAnalysis')}</h2>
              <SwimmingPhysiologicalAnalysis
                completed={summary.completed}
                metrics={summary.metrics}
                laps={summary.laps}
                athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
                activityId={activityType?.id}
                date={activity?.date || activityType?.date}
              />
            </div>
          )}

          {summary?.records?.length > 0 && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('swimming.zoneDistribution')}</h2>
              <ZoneDistributionChartSwimming userId={userId} fileId={activity.completed.fileId} />
            </div>
          )}

          {summary?.laps?.length > 0 && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('swimming.lapsDetail')}</h2>
              <LapsTableSwimming laps={summary.laps} />
            </div>
          )}

          {summary?.records?.length > 0 && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('elevationProfile.hrLapHistogramTitle')}</h2>
              <SwimmingHeartRateHistogram fileId={activity.completed?.fileId} records={summary.records} />
            </div>
          )}

          {activity.completed?.fileId && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('elevationProfile.hrTimeChartTitle')}</h2>
              <StrengthHeartRateHistogram fileId={activity.completed.fileId} />
            </div>
          )}


          {summary?.laps?.length > 0 && (
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('swimming.dashboard')}</h2>
              <SwimmingDashboard laps={summary.laps} />
            </div>
          )}

          {activity.completed?.fileId && (
            <WellnessForm
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              date={activity?.date || activityType?.date}
            />
          )}
          <SessionAiSummaryPanel
            key={activity?.completed?.fileId || "swim-ai"}
            sport="swimming"
            athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
            activityId={activityType?.id}
            date={activity?.date || activityType?.date}
            completed={summary?.completed}
            metrics={summary?.metrics}
          />
        </Modal.Body>

      <Modal.Footer>
        <SwimmingActions onSave={handleSave} onDelete={handleDelete} onHide={onHide} />
      </Modal.Footer>
    </Modal>
  );
};

export default SwimmingActivityModal;
